<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('app-repository')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Edit App Details</h1>
        </header>

        <section class="module__body">
            <form
                class="full-width"
                @submit.prevent="updateApp"
            >
                <div class="form-flex">
                    <div class="form-flex-group">
                        <div class="form-group">
                            <label for="type">App Type</label>
                            <select
                                id="type"
                                v-model="type"
                                disabled
                            >
                                <option value="atak_plugin">goTenna Pro ATAK Plugin</option>
                                <option value="atak_core">ATAK Core</option>
                                <option value="pro_app">goTenna Pro</option>
                                <option value="generic">Generic App</option>
                                <option value="generic_atak_plugin">Generic ATAK Plugin</option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label for="display_name">Title</label>
                            <input
                                v-model="display_name"
                                type="text"
                                required
                                maxlength="60"
                            />
                        </div>

                        <div
                            v-if="!hiddenFields.version"
                            class="form-group"
                        >
                            <label for="version">Version</label>
                            <input
                                v-model="version"
                                type="text"
                                maxlength="32"
                            />
                        </div>

                        <div
                            v-if="!hiddenFields.atak_version"
                            class="form-group"
                        >
                            <label for="atak_version">ATAK Version</label>
                            <input
                                v-model="atak_version"
                                type="text"
                                maxlength="32"
                            />
                        </div>

                        <div
                            v-if="!hiddenFields.gotenna_version"
                            class="form-group"
                        >
                            <label for="gotenna_version">goTenna Version</label>
                            <select
                                id="gotenna_version"
                                v-model="gotenna_version"
                            >
                                <option value=""></option>
                                <option
                                    v-for="(userAgent, index) in userAgents"
                                    :key="`${index}`"
                                    :value="userAgent"
                                >
                                    {{ userAgent }}
                                </option>
                            </select>
                        </div>

                        <div class="form-group">
                            <label for="build_number">Build Number</label>
                            <input
                                v-model="build_number"
                                type="text"
                                maxlength="32"
                            />
                        </div>

                        <div
                            v-if="!hiddenFields.firmware_version"
                            class="form-group"
                        >
                            <label for="firmware_version">Firmware Version</label>
                            <ToggleButtonList
                                :selectedValues="firmware_version"
                                :allValues="sortedFirmware"
                                @update="firmware_version = $event"
                            />
                        </div>
                    </div>

                    <div class="form-flex-group">
                        <div class="form-group">
                            <label for="release_date">Release Date</label>
                            <input
                                id="release_date"
                                v-model="release_date"
                                type="date"
                                required
                            />
                        </div>

                        <div
                            v-show="is_link"
                            class="form-group"
                        >
                            <label for="link">Link</label>
                            <input
                                id="link"
                                v-model="link"
                                type="url"
                                :required="is_link"
                            />
                        </div>

                        <div
                            v-show="!is_link"
                            class="form-group"
                        >
                            <label for="filename">Filename</label>
                            <input
                                v-model="filename"
                                type="text"
                                maxlength="191"
                                disabled
                            />
                        </div>

                        <div class="form-group">
                            <label for="version">Make Visible to Admins</label>
                            <slider-toggle-field
                                name="visibleToAdmins"
                                label=""
                                :value="visible_to_admins"
                                @update="visible_to_admins = $event"
                            ></slider-toggle-field>
                        </div>

                        <div class="form-group">
                            <label for="version">Make Visible to goKit Tablet App</label>
                            <slider-toggle-field
                                name="visibleToGoKits"
                                label=""
                                :value="visible_to_gokits"
                                @update="visible_to_gokits = $event"
                            ></slider-toggle-field>
                        </div>
                    </div>
                </div>

                <form-errors :response="errorResponse"></form-errors>

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :class="{ active: submitted }"
                        :disabled="!formLoaded"
                    >
                        <div>Update</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import FormErrors from '@/components/FormErrors.vue'
import SliderToggleField from '@/components/SliderToggleField.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import ToggleButtonList from '@/components/ToggleButtonList.vue'

export default {
    components: {
        FormErrors,
        SliderToggleField,
        ToggleButtonList,
    },

    mixins: [canToast, hasBackButton],

    data() {
        return {
            id: this.$route.params.id,
            filename: null,
            display_name: null,
            version: null,
            atak_version: null,
            gotenna_version: null,
            firmware_version: [],
            build_number: null,
            visible_to_admins: null,
            visible_to_gokits: null,
            release_date: null,
            type: '',
            link: null,
            is_link: false,
            errorResponse: null,
            submitted: false,
            sortedFirmware: [],
            allUserAgents: [],
            formLoaded: false,
        }
    },

    computed: {
        userAgents() {
            let result = []

            let productType = ''

            switch (this.type) {
                case 'pro_app':
                    productType = 'goTennaPro'
                    break
                case 'atak_plugin':
                    productType = 'goTennaATAK'
                    break
                default:
                    break
            }

            if (productType) {
                result = this.allUserAgents
                    .filter((userAgent) => userAgent.product.user_agent === productType)
                    .map((d) => d.version)
            }

            return result
        },

        hiddenFields() {
            const result = {
                atak_version: false,
                firmware_version: false,
                gotenna_version: false,
                version: false,
            }

            switch (this.type) {
                case 'pro_app':
                    result.atak_version = true
                    result.version = true
                    break
                case 'atak_plugin':
                    result.atak_version = true
                    result.version = true
                    break
                case 'atak_core':
                    result.firmware_version = true
                    result.gotenna_version = true
                    result.version = true
                    break
                case 'generic_atak_plugin':
                    result.firmware_version = true
                    result.gotenna_version = true
                    break
                case 'generic':
                    result.firmware_version = true
                    result.gotenna_version = true
                    break
                default:
                    break
            }

            return result
        },
    },

    mounted() {
        const gotennaVersion = this.$http.get('user-agents').then(({ data }) => {
            this.allUserAgents = data
        })

        const firmware = this.$http.get('firmware').then(({ data }) => {
            // create sorted array of objects
            // with keys id and label
            this.sortedFirmware = data.data
                .map(({ id, version }) => {
                    return { id, version }
                })
                .sort((a, b) => (a.label > b.label ? 1 : -1))
        })

        const app = this.$http.get(`/app-repository/${this.id}`).then(({ data }) => {
            this.filename = data.filename
            this.display_name = data.display_name
            this.version = data.version
            this.firmware_version = data.firmware.map((item) => item.id)
            this.atak_version = data.atak_version
            this.gotenna_version = data.gotenna_version
            this.build_number = data.build_number
            this.visible_to_admins = Boolean(data.visible_to_admins)
            this.visible_to_gokits = Boolean(data.visible_to_gokits)
            this.release_date = data.release_date
            this.type = data.type
            this.link = data.is_link ? data.filename : null
            this.is_link = Boolean(data.is_link)
        })

        Promise.all([gotennaVersion, firmware, app]).then(() => {
            this.formLoaded = true
        })
    },

    methods: {
        updateApp() {
            if (this.submitted || !this.formLoaded) {
                return
            }

            this.submitted = true

            const data = {
                display_name: this.display_name,
                version: this.version,
                firmware_version: this.firmware_version,
                atak_version: this.atak_version,
                gotenna_version: this.gotenna_version,
                build_number: this.build_number,
                visible_to_admins: Number(this.visible_to_admins),
                visible_to_gokits: Number(this.visible_to_gokits),
                release_date: this.release_date,
                type: this.type,
                is_link: Number(this.is_link),
                link: this.link,
            }

            // remove hidden fields
            Object.keys(this.hiddenFields)
                .filter((fieldName) => this.hiddenFields[fieldName])
                .forEach((fieldName) => {
                    delete data[fieldName]
                })

            this.$http
                .put(`/app-repository/${this.id}`, data)
                .then(() => {
                    this.toast('App successfully uploaded!')
                    this.$router.push(`/app-repository`)
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },
    },
}
</script>
